// Icon set — paths extracted verbatim from the signed-off mockups
// (mockups/01-overview.html sidebar + row icons). 16×16, stroke currentColor.

import type { ReactElement, ReactNode, SVGProps } from 'react';
import type { DeliverableType } from '../../shared/deliverables';
import { LEAD_SOURCE_LABELS, type LeadSource } from '../../shared/types';

export const DrivesIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}><rect x="2" y="3" width="12" height="10" rx="1.5" /><path d="M4.5 9.5h.01M7 9.5h4.5" /></Icon>
);

function Icon({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" {...props}>
      {children}
    </svg>
  );
}

export const MarketingIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2.5 6.5v3h2l5 3v-9l-5 3h-2z" />
    <path d="M12 6a2.5 2.5 0 0 1 0 4" />
  </Icon>
);

export const OverviewIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2" y="2" width="5" height="5" rx="1.2" />
    <rect x="9" y="2" width="5" height="5" rx="1.2" />
    <rect x="2" y="9" width="5" height="5" rx="1.2" />
    <rect x="9" y="9" width="5" height="5" rx="1.2" />
  </Icon>
);

export const LeadsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2 4h12M4.5 8h7M7 12h2" />
  </Icon>
);

export const ProjectsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M2 6.5h12" />
  </Icon>
);

export const TasksIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M5.5 8l1.8 1.8L10.8 6" />
  </Icon>
);

export const ClientsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="5.5" r="2.5" />
    <path d="M3 13.5c0-2.5 2.2-4 5-4s5 1.5 5 4" />
  </Icon>
);

export const CrewIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="6" cy="5.5" r="2.3" />
    <circle cx="11.5" cy="6.7" r="1.7" />
    <path d="M2 13.5c0-2.4 1.9-3.9 4-3.9s4 1.5 4 3.9M9.9 9.9c1.7.2 3.1 1.5 3.4 3.1" />
  </Icon>
);

export const FinanceIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M8 1.5v13M11 4.5c0-1.1-1.3-2-3-2s-3 .9-3 2 1 1.8 3 2 3 1 3 2.2-1.3 2-3 2-3-.9-3-2" />
  </Icon>
);

export const DashboardsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2 13.5V9M6 13.5V5.5M10 13.5V7.5M14 13.5V2.5" />
  </Icon>
);

export const ScriptsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M4 2h6l3 3v9H4z" />
    <path d="M10 2v3h3" />
  </Icon>
);

/** Stacked pages — the Documents library (scripts + call sheets). */
export const DocumentsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M6 4.5h5.5l2 2V14.5H6z" />
    <path d="M4 12V2.5h5.5" />
  </Icon>
);

export const ReviewsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2" y="3" width="12" height="9.5" rx="1.5" />
    <path d="M4.5 7.8l2.1 2.1L11.5 5" />
  </Icon>
);

export const CallSheetsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2.5" y="3" width="11" height="10.5" rx="1.5" />
    <path d="M2.5 6.5h11M5.5 1.5v3M10.5 1.5v3" />
  </Icon>
);

export const ProposalsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2.5 13.5l1-3.5 7-7a1.4 1.4 0 012 2l-7 7z" />
  </Icon>
);

export const MediaIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2" y="4.5" width="12" height="8" rx="1.5" />
    <path d="M6.8 6.7l3.4 1.8-3.4 1.8z" strokeLinejoin="round" />
  </Icon>
);

// Paper plane — the Outreach tab (2026-08-11 mockups, #i-outreach).
export const OutreachIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M14 2L7.2 8.9" />
    <path d="M14 2l-4.4 12-2.4-5.1L2 6.5z" strokeLinejoin="round" />
  </Icon>
);

export const SocialIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="12" cy="3.8" r="2" />
    <circle cx="4" cy="8" r="2" />
    <circle cx="12" cy="12.2" r="2" />
    <path d="M5.8 7.1l4.4-2.2M5.8 8.9l4.4 2.2" />
  </Icon>
);

export const AutomationsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M8.8 1.5L3.2 9h3.6l-1.4 5.5L13 6.6H9.2z" strokeLinejoin="round" />
  </Icon>
);

export const MoonIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M14 8.5a6 6 0 1 1-6.5-6.5A4.7 4.7 0 0 0 14 8.5z" />
  </Icon>
);

export const SunIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="2.7" />
    <path d="M8 1.3v1.4M8 13.3v1.4M2.8 2.8l1 1M12.3 12.3l1 1M1.3 8h1.4M13.3 8h1.4M2.8 13.2l1-1M12.3 3.7l1-1" />
  </Icon>
);

export const NoteIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M3 2.5h10v11H3zM5.5 6h5M5.5 8.5h5M5.5 11h3" />
  </Icon>
);

export const SettingsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="2.2" />
    <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
  </Icon>
);

export const AlertIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M8 4.8v3.6M8 10.9v.2" />
  </Icon>
);

export const ClockIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M8 4.5V8l2.4 1.6" />
  </Icon>
);

// Calendar-strip item kinds (week-one feedback §9 mockups: #i-shoot,
// #i-edit, #i-due) — a camera, an edit-timeline splice and a delivery box.

export const ShootIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path
      d="M2 5h7a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm8 2.5 4-2v5l-4-2z"
      strokeLinejoin="round"
    />
  </Icon>
);

export const EditIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="4.5" cy="4.5" r="2" />
    <circle cx="4.5" cy="11.5" r="2" />
    <path d="M6 6l8 6M6 10l8-6" strokeLinecap="round" />
  </Icon>
);

export const DueIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2 5.5 8 2l6 3.5v5L8 14l-6-3.5z M2 5.5 8 9l6-3.5 M8 9v5" strokeLinejoin="round" />
  </Icon>
);

export const StatsIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M2 13.5h12" />
    <path d="M2.5 10.5l3.5-3.5 2.5 2 4.5-5.5" />
    <path d="M10 3.5h3v3" />
  </Icon>
);

// ── Mobile set (mobile overhaul Phase 0) — the 24-grid symbols from
//    docs/mockups-mobile-overhaul-2026-09.html, verbatim. Rounded caps,
//    1.75 stroke; sized by the consumer (tab bar 24px, sheet rows 20px). ──

function MIcon({ children, strokeWidth = 1.75, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

export const MHomeIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 11.5 12 5l8 6.5" /><path d="M6.5 10.5V19h11v-8.5" /></MIcon>
);
export const MTasksIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m8.5 12.5 2.5 2.5 5-5.5" /></MIcon>
);
export const MLeadsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 5h16l-6 7v6l-4 2v-8z" /></MIcon>
);
export const MProjectsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="3.5" y="5" width="17" height="14" rx="2.5" /><path d="M3.5 9.5h17M8 5v14M16 5v14" /></MIcon>
);
/** Three horizontal dots — the bar's More tab. */
export const MoreIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <circle cx="6" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="18" cy="12" r="1.8" />
  </svg>
);
/** Three vertical dots — the page "···" menu button. */
export const DotsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <circle cx="12" cy="6" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="18" r="1.8" />
  </svg>
);
export const PlusIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon strokeWidth={2.25} {...p}><path d="M12 5v14M5 12h14" /></MIcon>
);
export const SearchIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" /></MIcon>
);
export const FilterIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 7h16M7 12h10M10 17h4" /></MIcon>
);
export const SortIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M7 4v16m0 0-3-3m3 3 3-3M17 20V4m0 0-3 3m3-3 3 3" /></MIcon>
);
export const BackIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon strokeWidth={2} {...p}><path d="m14 6-6 6 6 6" /></MIcon>
);
export const MAlertIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 8v5M12 16h.01" /></MIcon>
);
export const MClockIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></MIcon>
);
export const MInvoiceIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M6 3.5h9l4 4V20.5H6z" /><path d="M15 3.5v4h4M9 12h6M9 16h6" /></MIcon>
);
export const CalendarIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></MIcon>
);
export const CheckIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon strokeWidth={2.25} {...p}><path d="m5 12.5 4.5 4.5L19 7.5" /></MIcon>
);
export const XIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon strokeWidth={2} {...p}><path d="M6 6l12 12M18 6 6 18" /></MIcon>
);
export const SnoozeIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M14 4.5A8 8 0 1 0 19.5 10 6.5 6.5 0 0 1 14 4.5z" /></MIcon>
);
export const MNoteIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M5 4.5h14v15H5z" /><path d="M8.5 9h7M8.5 12.5h7M8.5 16h4" /></MIcon>
);
export const MoveIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h4l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" /></MIcon>
);
export const TrashIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M5 7h14M9.5 7V4.5h5V7M7 7l1 13h8l1-13" /></MIcon>
);
export const MClientsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="9.5" cy="9" r="3.5" /><path d="M3.5 19.5a6 6 0 0 1 12 0M16 6.5a3.5 3.5 0 0 1 0 6.5M20.5 19.5a6 6 0 0 0-4-5.6" /></MIcon>
);
export const MCrewIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 8.5h11v9H4z" /><path d="m15 11 5-2.5v8L15 14" /></MIcon>
);
export const MFinanceIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M12 4v16M16 8a3 3 0 0 0-3-2h-2.5a2.5 2.5 0 0 0 0 5h3a2.5 2.5 0 0 1 0 5H11a3 3 0 0 1-3-2" /></MIcon>
);
export const MStatsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 19h16M7 16V10M12 16V6M17 16v-4" /></MIcon>
);
export const MSocialIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="4" y="4" width="16" height="16" rx="4.5" /><circle cx="12" cy="12" r="3.5" /><path d="M16.5 7.5h.01" /></MIcon>
);
export const MDashboardsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></MIcon>
);
export const MAutomationsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M13 3 5 13.5h6L11 21l8-10.5h-6z" /></MIcon>
);
export const MSettingsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2.5M12 18v2.5M3.5 12H6M18 12h2.5M6 6l1.8 1.8M16.2 16.2 18 18M6 18l1.8-1.8M16.2 7.8 18 6" /></MIcon>
);
export const MMoonIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" /></MIcon>
);
export const MOutreachIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M20 4 4 10.5l7 2.5 2.5 7z" /><path d="M20 4 11 13" /></MIcon>
);
export const MScriptsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M7 4h10v16H7z" /><path d="M10 8h4M10 11.5h4M10 15h2.5" /></MIcon>
);
export const MDocumentsIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M9 7h8l3 3v11H9z" /><path d="M6 17V3h8" /><path d="M12 13.5h5M12 17h3.5" /></MIcon>
);
export const ArrowIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon strokeWidth={2} {...p}><path d="M5 12h14m-6-6 6 6-6 6" /></MIcon>
);
export const FlagIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M6 20V4h11l-2.5 4 2.5 4H6" /></MIcon>
);
export const RepeatIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M17 4l3 3-3 3" /><path d="M4 11V9a2 2 0 0 1 2-2h14M7 20l-3-3 3-3" /><path d="M20 13v2a2 2 0 0 1-2 2H4" /></MIcon>
);
export const SyncIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M20 12a8 8 0 1 1-2.34-5.66" /><path d="M20 4v4h-4" /></MIcon>
);
export const SelectIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="7" cy="7" r="2.5" /><circle cx="7" cy="17" r="2.5" /><path d="M13 7h7M13 17h7" /></MIcon>
);
export const ConvertIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M4 7h11l-3-3M20 17H9l3 3" /></MIcon>
);
export const LostIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><circle cx="12" cy="12" r="8.5" /><path d="m9 9 6 6M15 9l-6 6" /></MIcon>
);
export const LinkIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><path d="M10 14 14 10M8 12l-2 2a3 3 0 0 0 4 4l2-2M16 12l2-2a3 3 0 0 0-4-4l-2 2" /></MIcon>
);
export const ExpenseIcon = (p: SVGProps<SVGSVGElement>) => (
  <MIcon {...p}><rect x="3.5" y="6" width="17" height="12" rx="2.5" /><path d="M3.5 10h17M7 14.5h3" /></MIcon>
);

export const MeetingsIcon = (p: SVGProps<SVGSVGElement>) => <Icon {...p}><rect x="1.5" y="3" width="9" height="10" rx="2"/><path d="m10.5 6 4-2v8l-4-2M4 6h4M4 9h3"/></Icon>;

// ── Deliverable types (feedback round three, Option A cards) — 16-grid,
//    rounded joins; tinted per type by the card's icon tile. ──
const typeIcon = (children: ReactNode) => (p: SVGProps<SVGSVGElement>) => (
  <Icon strokeLinecap="round" strokeLinejoin="round" {...p}>{children}</Icon>
);
export const DELIVERABLE_TYPE_ICONS: Record<DeliverableType, (p: SVGProps<SVGSVGElement>) => ReactElement> = {
  video: typeIcon(<><rect x="1.8" y="3.5" width="12.4" height="9" rx="1.6" /><path d="M6.7 6.2v3.6L9.8 8z" /></>),
  cutdown: typeIcon(<><rect x="1.8" y="4.5" width="8" height="8" rx="1.4" /><path d="M5 2.5h7.2a1.6 1.6 0 0 1 1.6 1.6v6.4" /></>),
  stills: typeIcon(<><rect x="1.8" y="3.5" width="12.4" height="9" rx="1.6" /><circle cx="5.6" cy="6.6" r="1.1" /><path d="m2.4 11.6 3.7-3.4 2.6 2.3 1.8-1.5 3.1 2.7" /></>),
  document: typeIcon(<><path d="M4 1.8h5.2L12.5 5v9.2H4z" /><path d="M9 1.8V5.2h3.5M6 8.2h4.2M6 10.8h4.2" /></>),
  other: typeIcon(<><rect x="2.2" y="2.2" width="11.6" height="11.6" rx="2.4" /><path d="M5.5 8h5" /></>),
};
/** Six-dot drag grip — the deliverable card's reorder handle. */
export const GripIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 16 16" fill="currentColor" {...p}>
    <circle cx="5" cy="6" r="1.1" /><circle cx="8" cy="6" r="1.1" /><circle cx="11" cy="6" r="1.1" />
    <circle cx="5" cy="10" r="1.1" /><circle cx="8" cy="10" r="1.1" /><circle cx="11" cy="10" r="1.1" />
  </svg>
);

/** Calendar with a tick — a Locked-in lead's start date (round three §4). */
export const CalendarCheckIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}><rect x="2.5" y="3.5" width="11" height="10" rx="1.5" /><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3M6 10l1.4 1.4L10.2 8.6" /></Icon>
);

// ── Lead sources (feedback round three §4) — hand-drawn on the 16-grid with
//    rounded joins; each tinted by a per-source token (.src-*). "Other" has
//    no glyph on purpose. ──
const LEAD_SOURCE_GLYPHS: Partial<Record<LeadSource, ReactNode>> = {
  // Rounded tile with a lower-case f.
  facebook: <><rect x="2" y="2" width="12" height="12" rx="3" /><path d="M10.2 5.2h-.9c-1 0-1.5.5-1.5 1.5V14M6.3 8.6h3.6" /></>,
  // Globe: outline, meridian lens and equator.
  website: <><circle cx="8" cy="8" r="6" /><path d="M8 2c-1.7 1.7-2.5 3.7-2.5 6s.8 4.3 2.5 6c1.7-1.7 2.5-3.7 2.5-6S9.7 3.7 8 2zM2.3 8h11.4" /></>,
  // Speaking head: profile facing right with two sound arcs.
  referral: <><path d="M3 14v-2.1A4.6 4.6 0 0 1 1.9 8.8C1.9 6.2 3.8 4.3 6.3 4.3c2 0 3.5 1.3 3.5 3.1l.9 1.6h-.9v1.2c0 .7-.5 1.2-1.2 1.2H7.4V14" /><path d="M11.8 5.4c.6.8.6 1.9 0 2.7M13.4 4c1.2 1.6 1.2 3.9 0 5.5" /></>,
  // Repeat: two arcs with arrowheads, a loop.
  repeat_client: <><path d="M2.8 7.2A5.2 5.2 0 0 1 12 4.4l1.2 1.1M13.2 2.6v2.9h-2.9M13.2 8.8A5.2 5.2 0 0 1 4 11.6l-1.2-1.1M2.8 13.4v-2.9h2.9" /></>,
  // LinkedIn: rounded tile with "in".
  linkedin: <><rect x="2" y="2" width="12" height="12" rx="2.6" /><path d="M5.3 7.2v3.9M5.3 5.1v.01M7.8 11.1V7.2M7.8 9c0-1.1.7-1.8 1.7-1.8s1.5.7 1.5 1.8v2.1" /></>,
  // Instagram: camera tile, lens and flash dot.
  instagram: <><rect x="2" y="2" width="12" height="12" rx="3.6" /><circle cx="8" cy="8" r="2.7" /><path d="M11.3 4.7v.01" /></>,
};

/** Source glyph with a "Source: Facebook" tooltip; renders nothing for
    Other or a lead without a source. */
export function LeadSourceIcon({ source, size = 13 }: { source: LeadSource | null; size?: number }) {
  const glyph = source ? LEAD_SOURCE_GLYPHS[source] : undefined;
  if (!source || !glyph) return null;
  const label = `Source: ${LEAD_SOURCE_LABELS[source]}`;
  return (
    <span className={`src-icon src-${source}`} title={label} role="img" aria-label={label}>
      <Icon width={size} height={size} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{glyph}</Icon>
    </span>
  );
}
